import { DATA_ID, DATA_KEY, DynamicStore, load } from '@as-react/common-utils';
import { Logger } from '@as-react/logger';
import { RenderClientComponent, UnmountClientComponent } from './components/RenderClientComponent';
import { getAemConfigurationFromBrowserContext } from './utils/browser.util';
import { performanceMark, performanceMeasure } from './utils/performance.util';
export * from './components/WrapComponent';
export * from './components/wrappers/index';
export * from './utils/wrappers.util';
/**
 * Hydrates every server-rendered component inside `root`.
 *
 * Returns a dispose function that unmounts what this call hydrated. Callers that hydrate a
 * container they own (RowBanner, product placement copy) must invoke it when that container goes
 * away; the top-level page hydration can ignore it, as its roots live as long as the document.
 */
export const Hydrater = async ({ availableComponents, root, renderingTools, translationLang, i18n, }) => {
    if (root.querySelectorAll(`div[data-${DATA_ID}]`).length <= 0) {
        return () => { };
    }
    performanceMark('as-react-hydrate-start');
    const storeConfiguratorPromise = await renderingTools.redux.configureStore();
    const storeConfigurator = await storeConfiguratorPromise.default();
    const dynamicStore = new DynamicStore();
    const numeralInstance = await renderingTools.numeral();
    const hydratedNodes = await searchAndHydrateComponents({
        availableComponents,
        root,
        modules: {
            redux: {
                storeConfigurator,
                dynamicStore,
            },
            i18n: {
                initialLanguage: translationLang,
                initialI18nStore: i18n.services.resourceStore.data,
            },
            numeral: numeralInstance.default,
        },
    });
    performanceMark('as-react-hydrate-end');
    performanceMeasure('as-react-hydrate', 'as-react-hydrate-start', 'as-react-hydrate-end');
    return () => hydratedNodes.forEach(UnmountClientComponent);
};
/**
 * Hydrates `root` and returns a dispose function that is safe to use directly as a React effect
 * cleanup: disposing before hydration settles tears down whatever the in-flight pass mounts.
 *
 * Scoped callers should use this rather than awaiting `Hydrater` themselves, so the
 * already-disposed bookkeeping lives here instead of being repeated per call site.
 */
export const hydrateContainer = (init, onSettled, onError = Logger.logError) => {
    let disposed = false;
    let dispose;
    Hydrater(init)
        .then(hydraterDispose => {
        onSettled?.();
        return disposed ? hydraterDispose() : (dispose = hydraterDispose);
    })
        .catch(onError);
    return () => {
        disposed = true;
        dispose?.();
    };
};
const searchAndHydrateComponents = async ({ availableComponents, root, modules }) => {
    const aemConfiguration = getAemConfigurationFromBrowserContext();
    const hydratedNodes = [];
    const availableComponentKeys = Object.keys(availableComponents);
    const reactComponentKeysInsideElement = availableComponentKeys.filter(availableComponentKey => root.querySelectorAll(`div[data-${DATA_KEY}="${String(availableComponentKey)}"]`).length > 0);
    const components = reactComponentKeysInsideElement.reduce((acc, AEMScenes) => {
        const component = availableComponents[AEMScenes];
        if (component.serverOnly) {
            return acc;
        }
        acc.push(AEMScenes);
        return acc;
    }, []);
    for (const component of components) {
        const payloads = load(component, root);
        if (payloads && payloads.filter(Boolean).length > 0) {
            for (const payload of payloads) {
                const { node, data } = payload;
                if (!node) {
                    continue;
                }
                const dataWithContext = {
                    ...data,
                    context: {
                        ...(data?.context && typeof data.context === 'object' ? data.context : {}),
                        aemConfiguration,
                    },
                };
                const hydratedNode = await RenderClientComponent(availableComponents[component], dataWithContext, node, modules);
                // Only nodes this pass actually mounted; anything already owned by an earlier pass must not
                // end up in our dispose list.
                if (hydratedNode) {
                    hydratedNodes.push(hydratedNode);
                }
            }
        }
    }
    return hydratedNodes;
};
